import { useLazyQuery } from '@apollo/client/react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  GET_PROFESIONALES_PARA_EXCLUIR,
  adaptProfesionalesParaExcluirArray,
} from '../../../data/graphQL/queries/HonorarioFidelizado/getProfesionalesParaExcluir';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';

export const SelectorProfesionalesDialog = ({
  open,
  idTipoProfesional,
  idDelegacion,
  idClaseMonitoreo,
  seleccionados,
  onAceptar,
  onCerrar,
}) => {
  const [profesionales, setProfesionales] = useState([]);
  const [txtFiltro, setTxtFiltro]         = useState('');
  const [seleccion, setSeleccion]         = useState([]);

  const [getProfesionales, { data: dataProfesionales, error: errorProfesionales, loading }] =
    useLazyQuery(GET_PROFESIONALES_PARA_EXCLUIR, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (!dataProfesionales) return;
    setProfesionales(adaptProfesionalesParaExcluirArray(dataProfesionales));
  }, [dataProfesionales]);
 
  useEffect(() => {
    if (!errorProfesionales) return;
    toast.error('No se pudo cargar la lista de profesionales.');
  }, [errorProfesionales]);

  useEffect(() => {
    if (!open || !idTipoProfesional) return;
    setSeleccion(seleccionados ?? []);
    setTxtFiltro('');
    getProfesionales({ variables: { idTipoProfesional, idDelegacion: idDelegacion ?? null, idClaseMonitoreo: idClaseMonitoreo ?? null } });
  }, [open, idTipoProfesional, idDelegacion, idClaseMonitoreo]);

  const filasFiltradas = useMemo(() => {
    if (!txtFiltro) return profesionales;
    const lower = txtFiltro.toLowerCase();
    return profesionales.filter((p) =>
      [p.apellido, p.nombre, p.tipoDocumento, p.numeroDoc, String(p.id)]
        .some((v) => v?.toLowerCase().includes(lower))
    );
  }, [profesionales, txtFiltro]);

  const toggleSeleccion = (id) =>
    setSeleccion((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleTodos = () => {
    const todosIds      = filasFiltradas.map((p) => p.id);
    const todosMarcados = todosIds.every((id) => seleccion.includes(id));
    setSeleccion(
      todosMarcados
        ? seleccion.filter((id) => !todosIds.includes(id))
        : [...new Set([...seleccion, ...todosIds])]
    );
  };

  const handleAceptar = () => {
    const profesionalesSeleccionados = profesionales.filter((p) => seleccion.includes(p.id));
    onAceptar(seleccion, profesionalesSeleccionados);
  };

  const columns = generateColumnsDefaultConfiguration([
    {
      field: 'sel', headerName: '', width: 50, sortable: false,
      renderHeader: () => {
        const todosIds = filasFiltradas.map((p) => p.id);
        const checked  = todosIds.length > 0 && todosIds.every((id) => seleccion.includes(id));
        return <Checkbox checked={checked} onChange={toggleTodos} size='small' />;
      },
      renderCell: ({ row }) => (
        <Checkbox
          checked={seleccion.includes(row.id)}
          onChange={() => toggleSeleccion(row.id)}
          size='small'
        />
      ),
    },
    { field: 'id',            headerName: 'Id',             width: 80  },
    { field: 'apellido',      headerName: 'Apellido',        width: 180 },
    { field: 'nombre',        headerName: 'Nombre',          width: 180 },
    { field: 'tipoDocumento', headerName: 'T. de Documento', width: 140 },
    { field: 'numeroDoc',     headerName: 'Número',          width: 120 },
  ]);

  return (
    <Dialog open={open} onClose={onCerrar} maxWidth='md' fullWidth>
      <DialogTitle>Selecciona el profesional a excluir</DialogTitle>

      <DialogContent dividers>
        <TextField
          label='Filtrar'
          size='small'
          value={txtFiltro}
          onChange={(e) => setTxtFiltro(e.target.value)}
          sx={{ mb: 2, width: 300 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <SearchIcon />
                {txtFiltro && (
                  <ClearIcon sx={{ cursor: 'pointer' }} onClick={() => setTxtFiltro('')} />
                )}
              </InputAdornment>
            ),
          }}
        />

        <DataGridGenerico
          columns={columns}
          rows={filasFiltradas}
          totalCount={filasFiltradas.length}
          loading={loading}
          toolbar={false}
          autoHeight
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCerrar} variant='outlined'>Cerrar</Button>
        <Button onClick={handleAceptar} variant='contained'>Aceptar</Button>
      </DialogActions>
    </Dialog>
  );
};